import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { CountryCode as LibPhoneCountryCode } from 'libphonenumber-js';

import { GET_COUNTRIES } from 'src/graphql/queries/countries.query';
import { CountryConfig } from 'src/lib/countryConfig';

type CountryCode = LibPhoneCountryCode | 'XI';

export type Country = {
    country: CountryCode;
    name: string;
    flag: string;
    callingCode: string;
    meta: CountryConfig;
    postalCodeType: 'POSTCODE' | 'EIRCODE' | 'ZIPCODE' | 'PIN';
    alpha2Code?: string;
    currencies?: {
        code: string;
        name: string;
        symbol: string;
    }[];
};

export const useCountries = (filterByAlpha2Codes?: string[], isAuthenticated?: boolean) => {
    const [countryList, setCountryList] = useState([]);
    const [fetchCountries, { loading: loadingCountries }] = useLazyQuery(GET_COUNTRIES, {
        fetchPolicy: 'cache-first',
        onCompleted: (data) => {
            localStorage.setItem('countries', JSON.stringify(data?.countries));
            setCountryList(data?.countries);
        },
    });

    const filterCountries = (countriesToFilter) => {
        if (filterByAlpha2Codes && filterByAlpha2Codes.length > 0) {
            return countriesToFilter.filter((country) => {
                return filterByAlpha2Codes.includes(country.alpha2Code);
            });
        }
        return countriesToFilter;
    };

    const countries: Country[] = useMemo(() => {
        if (countryList && !loadingCountries) {
            if (filterByAlpha2Codes && filterByAlpha2Codes.length > 0) {
                const filteredCountries = filterCountries(countryList);

                return filteredCountries.map((country) => {
                    return {
                        country: country.alpha2Code,
                        name: country.name,
                        flag: country.flags.svg,
                        callingCode: country.callingCodes[0].code,
                        postalCodeType: country.postalCodeType,
                    };
                });
            }
            return countryList.map((country) => {
                return {
                    country: country.alpha2Code,
                    name: country.name,
                    flag: country.flags.svg,
                    callingCode: country.callingCodes[0]?.code,
                    postalCodeType: country.postalCodeType,
                };
            });
        }

        return [];
    }, [countryList, loadingCountries]);

    const countryConfig: Record<CountryCode, CountryConfig> = useMemo(() => {
        let countries = countryList;

        if (countries.length === 0) {
            const storedCountries = localStorage.getItem('countries');
            countries = storedCountries ? JSON.parse(storedCountries) : [];
        }

        const config: Record<CountryCode, CountryConfig> = {} as Record<CountryCode, CountryConfig>;

        countries.forEach((country: Country) => {
            if (country.alpha2Code) {
                config[country.alpha2Code as CountryCode] = {
                    name: country.name,
                    defaultCurrency: country.currencies?.[0]?.code ?? 'EUR',
                    supportedCurrencies: country.currencies?.map((currency) => {
                        return currency.code;
                    }) ?? ['EUR'],
                    postalCodeType: country.postalCodeType,
                    phoneCode: country.callingCodes?.[0]?.code ?? '',
                };
            }
        });

        return config;
    }, [countryList]);

    const isValidPostalCodeType = (country: Partial<Country>) => {
        return (
            Object.prototype.hasOwnProperty.call(country, 'postalCodeType') &&
            typeof country.postalCodeType === 'string' &&
            country.postalCodeType.trim().length > 0
        );
    };

    useEffect(() => {
        const storedCountries = localStorage.getItem('countries');
        const parsedCountries = storedCountries ? JSON.parse(storedCountries) : [];

        const invalidCountries = parsedCountries.filter((country) => {
            return !isValidPostalCodeType(country);
        });

        const allHavePostalCodeType = invalidCountries.length === 0;

        const fetchData = async () => {
            try {
                fetchCountries();
                return;
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        if (isAuthenticated && (!storedCountries || !allHavePostalCodeType || loadingCountries)) {
            fetchData();
        } else if (storedCountries) {
            if (filterByAlpha2Codes && filterByAlpha2Codes.length > 0) {
                const filteredCountries = JSON.parse(storedCountries).filter((country) => {
                    return filterByAlpha2Codes.includes(country.alpha2Code);
                });
                setCountryList(filteredCountries);
                return;
            }
            setCountryList(JSON.parse(storedCountries));
        }
    }, [isAuthenticated, loadingCountries, fetchCountries, filterByAlpha2Codes]);

    return { countries, loadingCountries, countryConfig };
};
